import React, { useState, useRef, useEffect } from 'react';
import { useHandleClickOutside } from '../hooks';
import FloatingSelect from './FloatingSelect.js';
import validateEmail from '../lib/string/validateEmail.js';
import { useSelector } from 'react-redux';
import selectors from '../selectors/selectors.js';

const PillSelect = ({ options = [], value, placeholder, freeform, className, format, onChange = () => {}, multiple = false }) => {
  const isMobile = useSelector(selectors.mobile);

  const [focused, setFocused] = useState(false);
  const [optionsIndex, setOptionsIndex] = useState(0);
  const optionsRef = useRef(null);
  const pillRef = useRef(null);
  const searchInputRef = useRef(null);
  const pillSelectRef = useRef(null);
  const [search, setSearch] = useState('');

  const values = Array.isArray(value) ? value : [value];
  const active = !!search && options.length > 0;
  let activeOptions = options.filter((n) => values.includes(n.id));
  const extraOptions = values.filter((n) => !options.find((o) => o.id === n)).map((n) => ({ id: n, label: n }));
  if (freeform) {
    activeOptions.push(...extraOptions);
  }

  let filteredOptions =
    multiple && !!search
      ? options.filter((n) =>
          String(n.label || '')
            .toLowerCase()
            .includes(search.toLowerCase())
        )
      : options;

  filteredOptions = multiple ? filteredOptions.filter((n) => !values.includes(n.id)) : filteredOptions;

  if (freeform && filteredOptions.length < 1) {
    let valid = true;
    if (format === 'email') {
      valid = validateEmail(search);
    }
    if (valid) {
      filteredOptions.push({ id: search, label: search });
    }
  }

  const handleOptionClick = (option) => {
    setSearch('');
    setOptionsIndex(0);
    setFocused(false);
    if (!multiple) {
      return onChange(option);
    }
    if (activeOptions.find((n) => n.id === option.id)) {
      return onChange([...activeOptions.filter((n) => (freeform ? !n.id || n.id !== option.id : n.id !== option.id))]);
    }
    return onChange([...activeOptions, option]);
  };

  useEffect(() => {
    if (focused) {
      if (optionsRef.current) {
        optionsRef.current.scrollTop = 0;
      }
    }
  }, [focused]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchFocus = () => {
    setFocused(true);
  };

  const handleSearchBlur = () => {
    setFocused(false);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      setSearch('');
      setFocused(false);
    }
    if (e.key === 'Backspace' && multiple) {
      if (search === '' && activeOptions.length > 0) {
        onChange([...activeOptions.slice(0, activeOptions.length - 1)]);
      }
    }
  };

  const handleSearchKeyUp = (e) => {
    if (e.key === 'Enter' && search) {
      const option = filteredOptions[optionsIndex];
      if (option) {
        setSearch('');
        handleOptionClick(option);
        return;
      }
    }
    if (e.key === 'Escape') {
      setSearch('');
      setFocused(false);
      return;
    }
    let newIndex = (
      {
        ArrowUp: (n) => n - 1,
        ArrowDown: (n) => n + 1,
        PageUp: (n) => n - 7,
        PageDown: (n) => n + 7,
        Home: () => 0,
        End: () => filteredOptions.length - 1,
      }[e.key] || ((n) => n)
    )(optionsIndex);
    newIndex = newIndex < 0 ? 0 : newIndex;
    newIndex = newIndex > filteredOptions.length - 1 ? filteredOptions.length - 1 : newIndex;
    if (newIndex !== optionsIndex) {
      setOptionsIndex(newIndex);
    }
  };

  const handlePillClick = (option) => {
    setFocused(true);
  };

  const handlePillKeyup = (e, option) => {
    if (e.key === 'Enter') {
      e.target.click();
    }
    if (e.key === 'Backspace' && multiple) {
      handlePillCloseClick(e, option);
      setTimeout(() => {
        if (searchInputRef.current && searchInputRef.current.focus) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  };

  const handlePillCloseClick = (e, option) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isMobile) {
      setFocused(true);
    }
    const newValues = values.filter((n) => n !== option.id);
    onChange(activeOptions.filter((n) => newValues.includes(n.id)));
  };

  useHandleClickOutside(pillSelectRef, () => {
    setFocused((state) => (state === true ? false : state));
  });

  const handleFloatingSelectChange = (option) => {
    if ((option && option.id) || (multiple && Array.isArray(option))) {
      setSearch('');
      let finalOptions = option;
      if (multiple) {
        let existingIds = activeOptions.map((n) => n.id);
        let newOptions = option.filter((o) => !existingIds.includes(o.id));
        finalOptions = [...activeOptions, ...newOptions];
      }
      onChange(finalOptions);
      if (multiple && !isMobile) {
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }, 50);
      }
    }
  };

  const handleSelectClick = (e) => {
    if (multiple && [...e.target.classList].includes('pill-select') && searchInputRef.current) {
      searchInputRef.current.focus();
      setTimeout(() => {
        searchInputRef.current.blur();
        setTimeout(() => searchInputRef.current.focus(), 0);
      }, 0);
    }
  };

  return (
    <div className={`pill-select ${active ? 'active' : ''} ${multiple ? 'multiple' : ''}`} ref={pillSelectRef} onClick={handleSelectClick}>
      {activeOptions.map((n, idx) => (
        <div key={idx} className={'pill'} onClick={(e) => handlePillClick(n)} ref={pillRef} tabIndex={0} onKeyUp={(e) => handlePillKeyup(e, n)}>
          <div className={'pill-label'}>{n.label}</div>
          {multiple && (
            <div className={'close-button'} onClick={(e) => handlePillCloseClick(e, n)}>
              ✖
            </div>
          )}
        </div>
      ))}
      {(multiple || !activeOptions.length) && <input placeholder={placeholder} value={search} ref={searchInputRef} onChange={handleSearchChange} onFocus={handleSearchFocus} onBlur={handleSearchBlur} onKeyDown={handleSearchKeyDown} onKeyUp={handleSearchKeyUp} />}
      {(pillRef.current || searchInputRef.current) && <FloatingSelect className={`pill-select-floating-select ${className}`} triggers={[pillRef.current, searchInputRef.current]} options={options} filteredOptions={multiple ? filteredOptions : options} optionsIndex={optionsIndex} onChange={handleFloatingSelectChange} value={values} freeform={freeform} search={!multiple} multiple={multiple} active={multiple && active !== undefined ? active || focused : undefined} />}
    </div>
  );
};

export default PillSelect;
